import React from 'react';
import { Card, Stack, SxProps, Theme, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { useTranslation } from 'react-i18next';
import type { TutorLessonOption } from '../../models/Tutor';
import TutorLessonSessionTypeChip from './TutorLessonSessionTypeChip';

export interface TutorLessonOptionContentProps {
  option: TutorLessonOption;
  /** When true, omit the price row (e.g. pass instance context). */
  hidePrice?: boolean;
  /** When true, omit the description (e.g. compact pickers). */
  hideDescription?: boolean;
  /** Single-line name + price for closed select values. */
  compact?: boolean;
}

export const TutorLessonOptionContent: React.FC<TutorLessonOptionContentProps> = ({
  option,
  hidePrice = false,
  hideDescription = false,
  compact = false,
}) => {
  const { i18n } = useTranslation();
  const priceLabel = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: option.lessonPriceCurrency ?? 'PLN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(option.price);
  const description = hideDescription || compact ? '' : option.description?.trim() || '';

  if (compact) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ width: '100%', minWidth: 0 }}
      >
        <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
          {option.name}
        </Typography>
        {!hidePrice ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <PaymentsRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {priceLabel}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack spacing={0.75} minWidth={0}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {option.name}
          </Typography>
          <TutorLessonSessionTypeChip sessionType={option.sessionType} />
        </Stack>
        <Stack spacing={0.25} alignItems="flex-end" justifyContent="center" sx={{ flexShrink: 0 }}>
          {!hidePrice && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PaymentsRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {priceLabel}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {option.durationMinutes} min
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Stack>
  );
};

export interface TutorLessonOptionCardProps {
  option: TutorLessonOption;
  onClick?: () => void;
  /** When true, omit the price row (e.g. pass instance context). */
  hidePrice?: boolean;
  sx?: SxProps<Theme>;
  /** Extra content inside the card (e.g. assigned tutors on the host lesson view). */
  children?: React.ReactNode;
}

const TutorLessonOptionCard: React.FC<TutorLessonOptionCardProps> = ({
  option,
  onClick,
  hidePrice = false,
  sx,
  children,
}) => {
  const clickable = typeof onClick === 'function';

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        borderColor: 'divider',
        bgcolor: 'background.default',
        cursor: clickable ? 'pointer' : 'default',
        transition: clickable ? 'all 0.2s ease' : undefined,
        ...(clickable
          ? {
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: (theme) => theme.shadows[2],
              },
            }
          : {}),
        ...sx,
      }}
    >
      <Stack spacing={children ? 1.25 : 0}>
        <TutorLessonOptionContent option={option} hidePrice={hidePrice} />
        {children}
      </Stack>
    </Card>
  );
};

export default TutorLessonOptionCard;
